import { Box, Grid, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { UserDetails } from '../../constants/Common';
import firebaseApi from '../../firebase/firebaseApi';
import {
	DashboardFunctionsProps,
	GetEmployeeAttendance,
	GetEmployeesSignIn,
	GetGreetings,
	GetSignIn,
	GetSites,
	GetSitesBySiteLog,
	GetTimesheets,
} from '../Dashboard/DashboardFunc';
import { AppStoreLogo } from '../Logos/AppStoreLogo';
import { PlayStoreLogo } from '../Logos/PlayStoreLogo';

type DashboardDisplayProps = {
	userDetails: UserDetails;
};

/** Return the dashboard components for each accounttype.
 *
 * siteIds only used by seniorManagement.**/
const GetContentData = (
	userDetails: DashboardFunctionsProps['userDetails'],
	siteIDs: DashboardFunctionsProps['siteIDs'],
	firebaseApi: DashboardFunctionsProps['firebaseApi'],
): JSX.Element => {
	switch (userDetails.accountType) {
		case 'management':
			return (
				<>
					<GetTimesheets
						userDetails={userDetails}
						firebaseApi={firebaseApi}
					/>
					<GetSignIn
						userDetails={userDetails}
						firebaseApi={firebaseApi}
					/>
				</>
			);
		case 'seniorManagement':
			return (
				<>
					<GetSites
						userDetails={userDetails}
						firebaseApi={firebaseApi}
					/>
					<GetTimesheets
						userDetails={userDetails}
						firebaseApi={firebaseApi}
					/>
					<GetSignIn
						userDetails={userDetails}
						siteIDs={siteIDs}
						firebaseApi={firebaseApi}
					/>
				</>
			);

		case 'handler':
			return (
				<>
					<GetEmployeesSignIn
						userDetails={userDetails}
						firebaseApi={firebaseApi}
					/>
					<GetEmployeeAttendance
						userDetails={userDetails}
						firebaseApi={firebaseApi}
					/>
					<GetSitesBySiteLog
						userDetails={userDetails}
						firebaseApi={firebaseApi}
					/>
				</>
			);
		case 'thirdPartyWorker':
		case 'worker':
			return (
				<>
					<Typography variant="h5">
						Download the Trade Legion App to join thousands of staff
						getting paid faster today...
					</Typography>
					<Grid
						container
						alignItems="start"
						flexDirection="row"
						justifyContent="space-around"
						maxWidth="fit-content"
						mt={8}
						gap={2}>
						<AppStoreLogo height="40px" />
						<PlayStoreLogo height="40px" />
					</Grid>
				</>
			);
		default:
			return <Typography>Information not found</Typography>;
	}
};

const DashboardDisplay = ({
	userDetails,
}: DashboardDisplayProps): JSX.Element => {
	const [siteIDs, setSiteIDs] = useState<string[]>([]);
	const mountedRef = useRef(true);

	useEffect(() => {
		mountedRef.current = true;
		const fetchSites = async (): Promise<void> => {
			const ids = await firebaseApi.getActiveSitesByCompany(
				userDetails.companyID,
			);

			if (mountedRef.current) {
				setSiteIDs(ids.docs.map((doc) => doc.data().id));
			}
		};
		fetchSites();
		return () => {
			mountedRef.current = false;
		};
	}, [userDetails.companyID]);

	return (
		<Box id="content" sx={{ padding: 5 }}>
			<GetGreetings userDetails={userDetails} />
			<Grid id="data" pt={5} display="flex" direction="column" container>
				{GetContentData(userDetails, siteIDs, firebaseApi)}
			</Grid>
		</Box>
	);
};

export default DashboardDisplay;
